import "./index.css";
import { clsx } from "clsx";
import { InboxIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState, Text } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { notificationsCenter, useNotifications } from "../../model";
import { HeaderMenu } from "../HeaderMenu";
import { NotificationItem } from "../NotificationItem";
import { NotificationsSkeleton } from "../NotificationsSkeleton";

type IProps = {
  isMobile: boolean;
  onNavigate: (path: string) => void;
  onDeleteAll: () => void;
};

const LOAD_AHEAD = "0px 0px 60px 0px";

export const NotificationsPanel = ({
  isMobile,
  onNavigate,
  onDeleteAll,
}: IProps) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const items = useNotifications((s) => s.items);
  const loaded = useNotifications((s) => s.loaded);
  const loading = useNotifications((s) => s.loading);

  const isEmpty = loaded && !loading && items.length === 0;

  // The feed lives only while the panel is open; the unread counter keeps running
  useEffect(() => {
    return () => notificationsCenter.resetFeed();
  }, []);

  // Loads the first page, then the next whenever the sentinel is in view.
  // Re-created after each load: a short page leaves the sentinel visible,
  // and a fresh observer reports that right away. The model ignores calls
  // when there is nothing more to load.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          notificationsCenter.loadMore().catch(serverErrorHandler());
        }
      },
      // Desktop scrolls the list itself; the mobile drawer scrolls its body,
      // so there the viewport (clipped by the drawer) is the root
      { root: isMobile ? null : listRef.current, rootMargin: LOAD_AHEAD },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, isMobile]);

  return (
    <div className={clsx("notifications", isMobile && "notifications--mobile")}>
      {!isMobile && (
        <header className="notifications-header">
          <Text weight="bold" as="h2" className="notifications-header__title">
            {t("label.notifications")}
          </Text>
          <HeaderMenu onDeleteAll={onDeleteAll} />
        </header>
      )}
      <div className="notifications-list" ref={listRef}>
        {isEmpty ? (
          <EmptyState
            className="notifications-list__empty-state"
            icon={<InboxIcon size="2.25rem" />}
            text={t("banner.emptyNotifications.noItems.title")}
          />
        ) : (
          <ul
            className="notifications-list__items"
            aria-label={t("label.notifications")}
            aria-busy={loading}
          >
            {items.map((item) => (
              <NotificationItem
                key={item.id}
                item={item}
                isMobile={isMobile}
                onNavigate={onNavigate}
              />
            ))}
            {loading && <NotificationsSkeleton rows={loaded ? 1 : 2} />}
          </ul>
        )}
        <div
          ref={sentinelRef}
          className="notifications-list__sentinel"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};
